import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { fetchRequest, FetchError } from "#/lib/stream-api";
import { useAtomValue } from "jotai";
import { atoms } from "#/lib/atoms/atoms";
import type { components } from "#/types.ts/swagger";

type User = components["schemas"]["GetUserDto"];

const userQueryConfig = (accessToken: string | null, orgId: string | null) => ({
  queryKey: ["user", accessToken, orgId],
  queryFn: async () => {
    try {
      if (!accessToken) return null;
      const data = await fetchRequest(`/user`);
      return data;
    } catch (error) {
      if (error.message === "Email not verified") {
        return { _id: null, email_verified: false };
      }
      if (
        error.message?.includes("Not an org member") ||
        error.statusCode === 403
      ) {
        return null;
      }
      throw error;
    }
  },
});

/**
 * Suspense variant (default). Suspends until the user query resolves — use
 * inside a Suspense boundary.
 */
export const useUser = () => {
  const accessToken = useAtomValue(atoms.accessToken);
  const orgId = useAtomValue(atoms.orgId);

  const { data } = useSuspenseQuery<User>(userQueryConfig(accessToken, orgId));

  return { data };
};

/**
 * Non-suspense variant. Safe to call where suspending is undesirable (e.g. the
 * error-boundary fallback). Returns `undefined` while loading instead of
 * suspending.
 */
export const useUserQuery = () => {
  const accessToken = useAtomValue(atoms.accessToken);
  const orgId = useAtomValue(atoms.orgId);

  const { data } = useQuery<User>(userQueryConfig(accessToken, orgId));

  return { data };
};
